import { useMemo } from "react";
import "./ThirteenthReport.css";

const QTag = ({ q, a, impacts }) => (
  <div style={{display:'flex',flexWrap:'wrap',gap:'5px 10px',alignItems:'center',background:'#f0f4ff',border:'1px solid #c7d7fa',borderRadius:'6px',padding:'7px 12px',marginBottom:'12px',fontSize:'12px',lineHeight:1.5}}>
    <span style={{background:'#3b5bdb',color:'#fff',fontWeight:700,padding:'2px 8px',borderRadius:4,fontSize:'10px',whiteSpace:'nowrap'}}>📋 FORM QUESTION</span>
    <span style={{color:'#1e3a8a',fontStyle:'italic'}}>"{q}"</span>
    <span style={{color:'#6b7280',fontSize:'11px'}}>→</span>
    <span style={{color:'#047857',fontWeight:700}}>You answered: <strong>{a}</strong></span>
    <span style={{color:'#6b7280',fontSize:'11px'}}>→</span>
    <span style={{color:'#7c3aed',fontSize:'11px'}}>📊 Impacts: {impacts}</span>
  </div>
);

const PROB_BG   = { High:"#e74c3c", Medium:"#f59e0b", Low:"#059669" };
const IMPACT_BG = { High:"#c0392b", Medium:"#d97706", Low:"#047857" };

function buildRisks(res) {
  const competition    = (res?.competition    ?? "").toLowerCase();
  const cost           = (res?.cost           ?? "").toLowerCase();
  const legal          = (res?.legal          ?? "").replace(/[^A-Za-z0-9 ]/g,"").toLowerCase();
  const team           = (res?.team           ?? "").toLowerCase();
  const bm             = (res?.businessModel  ?? "").toLowerCase();
  const maturity       = (res?.productMaturity?? "").toLowerCase();
  const necessity      = (res?.necessity      ?? "").replace(/[^A-Za-z0-9 ]/g,"").toLowerCase();
  const unfair         = (res?.unfairAdvantage?? "").replace(/[^A-Za-z0-9 ]/g,"").toLowerCase();

  const highComp = competition.includes("high") || competition.includes("intense") || competition.includes("dominant");
  const highCost = cost.includes("high") || cost.includes("team");
  const legalNo  = legal.includes("no");
  const teamNo   = team.includes("-yes") === false && team.toLowerCase() !== "-yes";
  const earlyMat = maturity.includes("idea") || maturity.includes("poc") || maturity.includes("mvp");
  const noUnfair = unfair.includes("no") || unfair === "";
  const lowNec   = necessity.includes("nice") || necessity.includes("no");

  return [
    {
      cat: "Market Competition",
      prob: highComp ? "High" : "Medium",
      impact: "High",
      root: highComp
        ? "Existing established players with market share and brand equity in your sector"
        : "Emerging competitors validating the same market thesis simultaneously",
      mitigation: "Narrow your ICP to an underserved niche. Build switching costs early through integrations, data lock-in, or community. Position on speed, personalisation, or local advantage, not feature parity.",
    },
    {
      cat: "Product-Market Fit",
      prob: lowNec ? "High" : earlyMat ? "Medium" : "Low",
      impact: "High",
      root: lowNec
        ? "Current problem framing is a 'nice-to-have', not a 'must-solve' pain — low urgency reduces conversion and retention"
        : "PMF still being validated; customer segment or value proposition may need iteration",
      mitigation: "Run 50 discovery interviews before writing a line of code. Track 3 core PMF signals: Week 2 retention, NPS > 40, and organic referral rate > 15%. Pivot the segment before pivoting the product.",
    },
    {
      cat: "Financial Sustainability",
      prob: highCost ? "High" : "Medium",
      impact: "High",
      root: highCost
        ? "High team/infrastructure costs relative to early revenue; runway risk within 9–12 months"
        : "Revenue ramp slower than burn rate if customer acquisition costs are not controlled early",
      mitigation: "Target default-alive in 18 months. Keep burn below ₹3L/mo until first 10 paying customers. Explore revenue-based financing, govt grants (Startup India, TIDE), and non-dilutive capital before raising equity.",
    },
    {
      cat: "Legal & Compliance",
      prob: legalNo ? "High" : "Low",
      impact: "Medium",
      root: legalNo
        ? "Product not yet validated for legal, data privacy, or regulatory compliance — increases investor liability concern"
        : "Regulatory landscape may shift, especially in FinTech, HealthTech, EdTech, and FoodTech segments",
      mitigation: "Register under DPIIT Startup India for legal immunity benefits. Consult a startup-focused CA for GST, IP, and data localisation. If HealthTech/FinTech, get CDSCO/RBI/SEBI approval roadmap from Day 1.",
    },
    {
      cat: "Customer Acquisition",
      prob: bm.includes("b2c") || bm.includes("c2c") ? "High" : "Medium",
      impact: "High",
      root: bm.includes("b2c") || bm.includes("c2c")
        ? "B2C and C2C models require scale for unit economics — high CAC risk before organic loops kick in"
        : "B2B sales cycles are long (3–6 months) and require relationships, demos, and procurement approval",
      mitigation: "Build a content-led inbound engine before paid acquisition. For B2B: target SMEs with 2-week free trials, not enterprise. Track CAC Payback Period — keep below 12 months. Activate referral programs and partnerships early.",
    },
    {
      cat: "Team & Domain Knowledge",
      prob: teamNo ? "High" : "Low",
      impact: "Medium",
      root: teamNo
        ? "Core team lacks direct domain expertise — risk of misjudging customer needs and technical architecture"
        : "Single-founder or small team creates key-person dependency and execution bottlenecks at scale",
      mitigation: "Add a domain advisor or industry mentor to your cap table at 0.25–0.5% equity. Join a sector-specific accelerator (T-Hub, NSRCEL, IIM Bangalore NSRCEL) for mentorship and credibility. Document all processes to reduce key-person risk.",
    },
    {
      cat: "Scalability",
      prob: noUnfair ? "High" : earlyMat ? "Medium" : "Low",
      impact: "Medium",
      root: noUnfair
        ? "No clear unfair advantage means replication risk is high — a well-funded competitor can copy the model"
        : "Infrastructure, operations, or human capital may not scale linearly with demand at growth stage",
      mitigation: "Build your moat in one of: proprietary data, network effects, regulatory licensing, or distribution partnerships. Architect for scale from the start — use serverless, modular microservices, and multi-tenancy to avoid refactoring at Series A.",
    },
  ];
}

const ThirteenthReport = ({ res }) => {
  const risks = useMemo(() => buildRisks(res), [res]);
  const competition   = res?.competition    ?? "—";
  const legalVal      = (res?.legal ?? "").replace(/[^A-Za-z0-9 ]/g,"") || "—";
  const teamVal       = res?.team           ?? "—";
  const necessity     = (res?.necessity ?? "").replace(/[^A-Za-z0-9 ]/g,"") || "—";

  return (
    <div className="iv-rb-thirteenth-container">
      <div className="iv-rb-thirteenth-final-table">
        <span className="iv-rb-thirteenth-corner iv-rb-thirteenth-corner-top-left" />
        <span className="iv-rb-thirteenth-corner iv-rb-thirteenth-corner-top-right" />
        <span className="iv-rb-thirteenth-corner iv-rb-thirteenth-corner-bottom-left" />
        <span className="iv-rb-thirteenth-corner iv-rb-thirteenth-corner-bottom-right" />

        <div className="iv-rb-thirteenth-module-title">
          <span className="iv-rb-mod-prefix">Module 12:</span> Startup Risk Matrix &amp; Mitigation Playbook
          <span className="iv-rb-thirteenth-premium-tag">★ Report A+ Exclusive</span>
        </div>

        <QTag
          q="What is your competition level, legal compliance status, team domain knowledge, problem necessity, and business model?"
          a={`Competition: ${competition} · Legal: ${legalVal} · Team knowledge: ${teamVal.includes("-Yes") ? "Yes" : "No"} · Problem necessity: ${necessity} · Model: ${res?.businessModel || "—"}`}
          impacts="Risk probability scoring, mitigation priority, investor red-flag areas, compliance urgency, team credibility risk"
        />

        <div className="iv-rb-thirteenth-legend">
          <span className="iv-rb-risk-badge" style={{background:"#e74c3c"}}>High</span>&nbsp;High Risk &nbsp;|&nbsp;
          <span className="iv-rb-risk-badge" style={{background:"#f59e0b"}}>Medium</span>&nbsp;Moderate Risk &nbsp;|&nbsp;
          <span className="iv-rb-risk-badge" style={{background:"#059669"}}>Low</span>&nbsp;Low Risk
        </div>

        <div className="iv-rb-thirteenth-table-scroll-wrapper">
          <table className="iv-rb-thirteenth-inner-table">
            <thead>
              <tr>
                <th className="iv-rb-thirteenth-col-sno">S.NO</th>
                <th>Risk Category</th>
                <th style={{width:"90px",textAlign:"center"}}>Probability</th>
                <th style={{width:"90px",textAlign:"center"}}>Impact</th>
                <th>Root Cause</th>
                <th>Mitigation Strategy</th>
              </tr>
            </thead>
            <tbody>
              {risks.map((r, i) => (
                <tr key={i} className={i % 2 === 0 ? "iv-rb-thirteenth-row-odd" : "iv-rb-thirteenth-row-even"}>
                  <td className="iv-rb-thirteenth-col-sno">12 {String.fromCharCode(65 + i)}</td>
                  <td><strong>{r.cat}</strong></td>
                  <td style={{textAlign:"center"}}>
                    <span className="iv-rb-risk-badge" style={{background: PROB_BG[r.prob]}}>{r.prob}</span>
                  </td>
                  <td style={{textAlign:"center"}}>
                    <span className="iv-rb-risk-badge" style={{background: IMPACT_BG[r.impact]}}>{r.impact}</span>
                  </td>
                  <td className="iv-rb-thirteenth-root">{r.root}</td>
                  <td className="iv-rb-thirteenth-mitigation">{r.mitigation}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="iv-rb-thirteenth-footer">
          © MarsMate AI · Risk assessment is derived from your form inputs. High-probability + High-impact risks should be addressed before your next funding round.
        </div>
      </div>
    </div>
  );
};

export default ThirteenthReport;
